import React from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Link,
  Grid,
  Avatar,
  useTheme,
  alpha,
} from "@mui/material";
import {
  Business as BusinessIcon,
} from "@mui/icons-material";
import WSTextField, { TextType } from "../Components/TextBox/WSTextBox";
import backImg from "../images/login-bg.svg";
import { useForm } from "react-hook-form";
import { callAPI, customValidation, LoacalVariables, Method, setValue, validations } from "../commonFunctions/CommonFunctions";
import { useSnackbar } from "../Components/Snackbar/SnackBarContext";
import { useNavigate } from "react-router-dom";

interface LoginFormProps {
  onLogin: (accessToken: string, refreshToken?: string) => void;
}

const Login: React.FC<LoginFormProps> = ({ onLogin }) => {
  const theme = useTheme();
  const [showPassword, setShowPassword] = React.useState(false);
  const [loading, setLoading] = React.useState(false);

  const { showMessage } = useSnackbar();
  const navigate = useNavigate();

  const {
    control,
    register,
    handleSubmit,
    formState: { isValid },
  } = useForm({
    mode: "onChange",
    defaultValues: {
      email: "",
      password: "",
      rememberMe: false,
    },
  });

  const onSubmit = async (data: any) => {
    console.log("Login data:", data);
    try {
      setLoading(true);

      const request = {
        email: data.email,
        password: data.password,
      };

      const response = await callAPI(
        "Vyapar/User/login",
        Method.POST,
        request
      );
      console.log('Login response:', response); // Debug log to check the API response

      if (response) {
        onLogin(response?.data.accessToken, response?.data.refreshToken);

        setValue(LoacalVariables.UserId, response?.data?.user.id)
        setValue(LoacalVariables.UserType, response?.data?.user.role)
        setValue(LoacalVariables.Name, response?.data?.user.userName);
        setValue(LoacalVariables.Email, response?.data?.user.email)

        showMessage(
          response?.message,
          response.success === true ? "success" : "error"
        );

        if (response.success === true) {
          navigate("/");
          // window.location.href = "/"; // Force full page reload to reset state and load authenticated routes
        }
      }
    } catch (error: any) {
      console.error("Failed to login user:", error);
      showMessage(error.response?.data?.message || "Failed to login user", "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundImage: `url(${backImg})`,
        position: "relative",
        overflow: "hidden",
        "&::before": {
          content: '""',
          position: "absolute",
          width: "200%",
          height: "200%",
          background: `radial-gradient(circle, ${alpha(
            theme.palette.primary.main,
            0.05
          )} 0%, transparent 50%)`,
          animation: "pulse 15s ease-in-out infinite",
        },
        "@keyframes pulse": {
          "0%": {
            transform: "translate(-50%, -50%) rotate(0deg)",
          },
          "100%": {
            transform: "translate(-50%, -50%) rotate(360deg)",
          },
        },
      }}
    >
      <Card
        elevation={24}
        sx={{
          width: 450,
          borderRadius: 4,
          position: "relative",
          overflow: "visible",
          backdropFilter: "blur(10px)",
          background: alpha(theme.palette.background.paper, 0.95),
          boxShadow: `0 20px 40px ${alpha(theme.palette.common.black, 0.1)}`,
        }}
      >
        <Box
          sx={{
            position: "absolute",
            top: -30,
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 10,
          }}
        >
          <Avatar
            sx={{
              width: 70,
              height: 70,
              bgcolor: theme.palette.primary.main,
              boxShadow: `0 10px 20px ${alpha(
                theme.palette.primary.main,
                0.3
              )}`,
            }}
          >
            <BusinessIcon sx={{ fontSize: 40 }} />
          </Avatar>
        </Box>

        <CardContent sx={{ p: 5, pt: 6 }}>
          <Typography
            variant="h4"
            align="center"
            sx={{
              mb: 1,
              fontWeight: 700,
              background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
              backgroundClip: "text",
              WebkitBackgroundClip: "text",
              color: "transparent",
              letterSpacing: "-0.5px",
            }}
          >
            Welcome Back
          </Typography>

          <Typography
            variant="body2"
            align="center"
            color="text.secondary"
            sx={{ mb: 4 }}
          >
            Sign in to manage your business
          </Typography>

          <form onSubmit={handleSubmit(onSubmit)}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <WSTextField
                  className="form-control"
                  id="email"
                  type={TextType.Text}
                  rules={customValidation(false, validations.email)}
                  {...register("email")}
                  label="Email Address"
                  control={control}
                  textIcon="Email"

                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2,
                      backgroundColor: alpha(theme.palette.common.white, 0.1),
                      transition: "all 0.3s ease",
                      "&:hover": {
                        backgroundColor: alpha(theme.palette.common.white, 0.15),
                      },
                      "&.Mui-focused": {
                        backgroundColor: alpha(theme.palette.common.white, 0.2),
                      },
                    },
                  }}

                />
              </Grid>

              <Grid item xs={12}>
                <WSTextField
                  label="Password"
                  {...register("password", {
                    required: "Password is required",
                    minLength: {
                      value: 8,
                      message: "Password must be at least 8 characters",
                    },
                  })}
                  id="password"
                  name="password"
                  control={control}
                  textIcon="Password"
                  type={showPassword ? TextType.Text : TextType.Password}

                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2,
                      backgroundColor: alpha(theme.palette.common.white, 0.1),
                      transition: "all 0.3s ease",
                      "&:hover": {
                        backgroundColor: alpha(theme.palette.common.white, 0.15),
                      },
                      "&.Mui-focused": {
                        backgroundColor: alpha(theme.palette.common.white, 0.2),
                      },
                    },
                  }}
                />
              </Grid>
            </Grid>

            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                display: "block",
                textAlign: "center",
                mt: 3,
                mb: 2,
              }}
            >
              By clicking on the Login button I accept the{" "}
              <Link
                href="#"
                sx={{
                  color: theme.palette.primary.main,
                  textDecoration: "none",
                  fontWeight: 500,
                  "&:hover": {
                    textDecoration: "underline",
                  },
                }}
              >
                terms and conditions
              </Link>
            </Typography>

            <Button
              fullWidth
              variant="contained"
              color="error"
              type="submit"
              disabled={!isValid || loading}
              sx={{
                mt: 2,
                py: 1.5,
                fontWeight: 600,
                borderRadius: 2,
                fontSize: "1rem",
                textTransform: "uppercase",
                letterSpacing: 1,
                background: `linear-gradient(45deg, ${theme.palette.primary.main} 30%, ${theme.palette.secondary.light} 90%)`,
                boxShadow: `0 3px 15px ${alpha(theme.palette.primary.main, 0.4)}`,
                transition: "all 0.3s ease",
                "&:hover": {
                  background: `linear-gradient(45deg, ${theme.palette.secondary.main} 30%, ${theme.palette.primary.main} 90%)`,
                  boxShadow: `0 5px 20px ${alpha(theme.palette.primary.main, 0.6)}`,
                  transform: "translateY(-2px)",
                },
                "&:disabled": {
                  background: alpha(theme.palette.primary.main, 0.5),
                  boxShadow: "none",
                },
              }}
            >
              Login
            </Button>
          </form>
        </CardContent>
      </Card>
    </Box>
  );
};

export default Login;